import { DeleteOutlined, DownloadOutlined, EditOutlined, FileSearchOutlined, PlusOutlined, ReloadOutlined, UploadOutlined } from '@ant-design/icons';
import {
  Button,
  Card,
  Col,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  Upload,
  Progress,
  message
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { UploadFile } from 'antd/es/upload/interface';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, downloadInsuranceConditionCustomerReportPdf } from './api';
import type { InsuranceConditionClause, InsuranceConditionDocument, InsuranceProcessingJob, InsuranceProcessingStatus } from './types';

const { Text } = Typography;

const AZURE_DI_CONFIG_ERROR_PATTERN = /Azure Document Intelligence er ikke konfigureret/i;

function toInsuranceConditionsUiErrorMessage(error: unknown, fallback: string): string {
  const rawMessage = error instanceof Error ? error.message : typeof error === 'string' ? error : '';
  if (AZURE_DI_CONFIG_ERROR_PATTERN.test(rawMessage)) {
    return 'Azure Document Intelligence mangler serverkonfiguration (endpoint + nøgle). Kontakt en administrator.';
  }
  return rawMessage || fallback;
}

type ClauseMatchComparison = {
  riskLevel: 'LOW' | 'MEDIUM' | 'HIGH';
  summary: string;
  differences: string[];
  brokerWatch: string[];
  customerView?: {
    summary: string;
    chooseSourceAdvantages: string[];
    chooseSourceDisadvantages: string[];
    chooseTargetAdvantages: string[];
    chooseTargetDisadvantages: string[];
  } | null;
};

type ClauseMatchResult = {
  sourceClause: any;
  targetClause: any;
  similarity: number;
  comparison?: ClauseMatchComparison | null;
};

type CustomerMatchSummary = {
  source: { advantages: string[]; disadvantages: string[] };
  target: { advantages: string[]; disadvantages: string[] };
  note: string;
};

type CustomerReportPoint = {
  title: string;
  text: string;
  reference: string | null;
};

type CustomerComparisonReport = {
  title: string;
  conclusion: string;
  customerMeaning: string;
  source: { headline: string; advantages: CustomerReportPoint[]; disadvantages: CustomerReportPoint[] };
  target: { headline: string; advantages: CustomerReportPoint[]; disadvantages: CustomerReportPoint[] };
  brokerReviewPoints: string[];
  disclaimer: string;
};

type DocumentFormValues = {
  companyName: string;
  productName: string;
  versionLabel?: string;
  fileName: string;
  fileType?: string;
  fileSize?: number | null;
  storagePath?: string;
  extractedText?: string;
  extractionStatus: 'PENDING' | 'COMPLETED' | 'FAILED';
  extractionError?: string;
};

type UploadFormValues = {
  companyName: string;
  productName: string;
  versionLabel?: string;
};

type ClauseFormValues = {
  areaCode?: string;
  sectionTitle?: string;
  pageNo?: number | null;
  clauseOrder?: number | null;
  rawText: string;
};

type StructureFormValues = {
  covers?: 'true' | 'false' | 'null';
  coverageSummary?: string;
  exclusionsText?: string;
  deductibleAmount?: number | null;
  deductibleText?: string;
  maxCoverageAmount?: number | null;
  maxCoverageText?: string;
  requirementsText?: string;
  practicalNotes?: string;
  confidence?: number | null;
  model?: string;
  payloadText?: string;
};

const statusLabels: Record<InsuranceConditionDocument['extractionStatus'], string> = {
  PENDING: 'Afventer',
  COMPLETED: 'Klar',
  FAILED: 'Fejlet'
};

const statusColors: Record<InsuranceConditionDocument['extractionStatus'], string> = {
  PENDING: 'gold',
  COMPLETED: 'green',
  FAILED: 'red'
};

const processingLabels: Record<InsuranceConditionDocument['processingStatus'], string> = {
  IDLE: 'Ikke behandlet',
  PROCESSING: 'Behandler',
  READY: 'Klar til match',
  FAILED: 'Fejlet'
};

const processingColors: Record<InsuranceConditionDocument['processingStatus'], string> = {
  IDLE: 'default',
  PROCESSING: 'blue',
  READY: 'green',
  FAILED: 'red'
};

function formatDateTime(value: string | null | undefined) {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return new Intl.DateTimeFormat('da-DK', { dateStyle: 'medium', timeStyle: 'short' }).format(date);
}

function compactText(value: string | null | undefined, fallback = '—') {
  const trimmed = typeof value === 'string' ? value.trim() : '';
  return trimmed || fallback;
}

function fileSlug(value: string | null | undefined) {
  return (value || 'kunderesume')
    .toLowerCase()
    .replace(/æ/g, 'ae')
    .replace(/ø/g, 'oe')
    .replace(/å/g, 'aa')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'kunderesume';
}

function linesToArray(value: string | undefined): string[] {
  if (!value) {
    return [];
  }
  return value
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
}

function arrayToLines(value: unknown[] | null | undefined) {
  if (!Array.isArray(value) || value.length === 0) {
    return '';
  }
  return value.map((item) => (typeof item === 'string' ? item : JSON.stringify(item))).join('\n');
}

function normalizeDocument(raw: any): InsuranceConditionDocument {
  return {
    id: String(raw.id ?? ''),
    companyName: raw.companyName ?? raw.company_name ?? '',
    productName: raw.productName ?? raw.product_name ?? '',
    versionLabel: raw.versionLabel ?? raw.version_label ?? null,
    fileName: raw.fileName ?? raw.file_name ?? '',
    fileType: raw.fileType ?? raw.file_type ?? null,
    fileSize: raw.fileSize ?? raw.file_size ?? null,
    storagePath: raw.storagePath ?? raw.storage_path ?? null,
    extractedText: raw.extractedText ?? raw.extracted_text ?? null,
    extractionStatus: raw.extractionStatus ?? raw.extraction_status ?? 'PENDING',
    extractionError: raw.extractionError ?? raw.extraction_error ?? null,
    uploadedBy: raw.uploadedBy ?? raw.uploaded_by ?? null,
    uploadedAt: raw.uploadedAt ?? raw.uploaded_at ?? '',
    clauseCount: Number(raw.clauseCount ?? raw.clause_count ?? 0),
    processingStatus: raw.processingStatus ?? raw.processing_status ?? 'IDLE',
    processingError: raw.processingError ?? raw.processing_error ?? null,
    processingStartedAt: raw.processingStartedAt ?? raw.processing_started_at ?? null,
    processingCompletedAt: raw.processingCompletedAt ?? raw.processing_completed_at ?? null,
    segmentedAt: raw.segmentedAt ?? raw.segmented_at ?? null,
    structuredCount: Number(raw.structuredCount ?? raw.structured_count ?? 0),
    embeddedCount: Number(raw.embeddedCount ?? raw.embedded_count ?? 0)
  };
}

function normalizeProcessingJob(raw: any): InsuranceProcessingJob {
  return {
    id: String(raw.id ?? ''),
    documentId: raw.documentId ?? raw.document_id ?? '',
    jobType: raw.jobType ?? raw.job_type ?? 'SEGMENT',
    status: raw.status ?? 'PENDING',
    processedCount: Number(raw.processedCount ?? raw.processed_count ?? 0),
    totalCount: Number(raw.totalCount ?? raw.total_count ?? 0),
    errorMessage: raw.errorMessage ?? raw.error_message ?? null,
    startedAt: raw.startedAt ?? raw.started_at ?? null,
    completedAt: raw.completedAt ?? raw.completed_at ?? null
  };
}

function normalizeProcessingStatus(raw: any): InsuranceProcessingStatus {
  return {
    document: normalizeDocument(raw.document || {}),
    jobs: Array.isArray(raw.jobs) ? raw.jobs.map(normalizeProcessingJob) : [],
    progress: Number(raw.progress ?? 0),
    ready: Boolean(raw.ready)
  };
}

function normalizeClause(raw: any): InsuranceConditionClause {
  const structure = raw.structure || null;
  return {
    id: String(raw.id ?? ''),
    conditionDocumentId: raw.conditionDocumentId ?? raw.condition_document_id ?? '',
    companyName: raw.companyName ?? raw.company_name ?? '',
    productName: raw.productName ?? raw.product_name ?? '',
    areaCode: raw.areaCode ?? raw.area_code ?? null,
    sectionTitle: raw.sectionTitle ?? raw.section_title ?? null,
    pageNo: raw.pageNo ?? raw.page_no ?? null,
    rawText: raw.rawText ?? raw.raw_text ?? '',
    clauseOrder: raw.clauseOrder ?? raw.clause_order ?? null,
    hasEmbedding: Boolean(raw.hasEmbedding ?? raw.has_embedding ?? false),
    embeddingModel: raw.embeddingModel ?? raw.embedding_model ?? null,
    embeddedAt: raw.embeddedAt ?? raw.embedded_at ?? null,
    createdAt: raw.createdAt ?? raw.created_at ?? '',
    updatedAt: raw.updatedAt ?? raw.updated_at ?? '',
    structure: structure
      ? {
          id: String(structure.id ?? ''),
          clauseId: structure.clauseId ?? structure.clause_id ?? '',
          covers: structure.covers ?? null,
          coverageSummary: structure.coverageSummary ?? structure.coverage_summary ?? null,
          exclusions: Array.isArray(structure.exclusions) ? structure.exclusions : [],
          deductibleAmount: structure.deductibleAmount ?? structure.deductible_amount ?? null,
          deductibleText: structure.deductibleText ?? structure.deductible_text ?? null,
          maxCoverageAmount: structure.maxCoverageAmount ?? structure.max_coverage_amount ?? null,
          maxCoverageText: structure.maxCoverageText ?? structure.max_coverage_text ?? null,
          requirements: Array.isArray(structure.requirements) ? structure.requirements : [],
          practicalNotes: structure.practicalNotes ?? structure.practical_notes ?? null,
          confidence: structure.confidence ?? null,
          model: structure.model ?? null,
          payload: structure.payload && typeof structure.payload === 'object' ? structure.payload : {},
          analyzedAt: structure.analyzedAt ?? structure.analyzed_at ?? null,
          createdAt: structure.createdAt ?? structure.created_at ?? '',
          updatedAt: structure.updatedAt ?? structure.updated_at ?? ''
        }
      : null
  };
}

export default function InsuranceConditionsPage() {
  const [documents, setDocuments] = useState<InsuranceConditionDocument[]>([]);
  const [clauses, setClauses] = useState<InsuranceConditionClause[]>([]);
  const [processingStatuses, setProcessingStatuses] = useState<Record<string, InsuranceProcessingStatus>>({});
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [clausesLoading, setClausesLoading] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [documentModalOpen, setDocumentModalOpen] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [clauseModalOpen, setClauseModalOpen] = useState(false);
  const [structureModalOpen, setStructureModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadSaving, setUploadSaving] = useState(false);
  const [segmentLoading, setSegmentLoading] = useState(false);
  const [uploadFileList, setUploadFileList] = useState<UploadFile[]>([]);
  const [embeddingLoading, setEmbeddingLoading] = useState(false);
  const [structureLoadingClauseId, setStructureLoadingClauseId] = useState<string | null>(null);
  const [structureAnalyzing, setStructureAnalyzing] = useState(false);
  const [semanticLoading, setSemanticLoading] = useState(false);
  const [matchLoading, setMatchLoading] = useState(false);
  const [semanticQuery, setSemanticQuery] = useState('');
  const [semanticResults, setSemanticResults] = useState<Array<InsuranceConditionClause & { similarity: number }>>([]);
  const [matchTargetDocumentId, setMatchTargetDocumentId] = useState<string | null>(null);
  const [matchResults, setMatchResults] = useState<ClauseMatchResult[]>([]);
  const [reverseMatchResults, setReverseMatchResults] = useState<ClauseMatchResult[]>([]);
  const [matchCustomerSummary, setMatchCustomerSummary] = useState<CustomerMatchSummary | null>(null);
  const [matchCustomerReport, setMatchCustomerReport] = useState<CustomerComparisonReport | null>(null);
  const [customerReportDownloading, setCustomerReportDownloading] = useState(false);
  const [editingDocument, setEditingDocument] = useState<InsuranceConditionDocument | null>(null);
  const [editingClause, setEditingClause] = useState<InsuranceConditionClause | null>(null);
  const [structuringClause, setStructuringClause] = useState<InsuranceConditionClause | null>(null);
  const [documentForm] = Form.useForm<DocumentFormValues>();
  const [uploadForm] = Form.useForm<UploadFormValues>();
  const [clauseForm] = Form.useForm<ClauseFormValues>();
  const [structureForm] = Form.useForm<StructureFormValues>();

  const selectedDocument = useMemo(
    () => documents.find((document) => document.id === selectedDocumentId) || null,
    [documents, selectedDocumentId]
  );
  const matchTargetDocument = useMemo(
    () => documents.find((document) => document.id === matchTargetDocumentId) || null,
    [documents, matchTargetDocumentId]
  );
  const selectedProcessingStatus = selectedDocumentId ? processingStatuses[selectedDocumentId] || null : null;

  const loadDocuments = useCallback(async () => {
    setDocumentsLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchTerm.trim()) {
        params.set('search', searchTerm.trim());
      }
      const response = await api(`/api/insurance-conditions${params.toString() ? `?${params.toString()}` : ''}`);
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error((data as any)?.error || 'Kunne ikke hente betingelser');
      }
      const nextDocuments = Array.isArray(data) ? data.map(normalizeDocument) : [];
      setDocuments(nextDocuments);
      if (selectedDocumentId && !nextDocuments.some((document) => document.id === selectedDocumentId)) {
        setSelectedDocumentId(null);
        setClauses([]);
      }
    } catch (error: any) {
      message.error(error?.message || 'Kunne ikke hente betingelser');
    } finally {
      setDocumentsLoading(false);
    }
  }, [searchTerm, selectedDocumentId]);

  const loadClauses = useCallback(async (documentId: string) => {
    setClausesLoading(true);
    try {
      const response = await api(`/api/insurance-conditions/${documentId}/clauses`);
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error((data as any)?.error || 'Kunne ikke hente klausuler');
      }
      setClauses(Array.isArray(data) ? data.map(normalizeClause) : []);
    } catch (error: any) {
      message.error(error?.message || 'Kunne ikke hente klausuler');
    } finally {
      setClausesLoading(false);
    }
  }, []);

  const loadProcessingStatus = useCallback(async (documentId: string) => {
    const response = await api(`/api/insurance-conditions/${documentId}/process-status`);
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error((data as any)?.error || 'Kunne ikke hente behandlingsstatus');
    }
    const status = normalizeProcessingStatus(data);
    setProcessingStatuses((current) => ({ ...current, [documentId]: status }));
    setDocuments((current) => current.map((document) => (document.id === documentId ? status.document : document)));
    return status;
  }, []);

  const startDocumentProcessing = useCallback(async (documentId: string, restart = true) => {
    const response = await api(`/api/insurance-conditions/${documentId}/process`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ restart })
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error((data as any)?.error || 'Kunne ikke starte behandling');
    }
    await loadProcessingStatus(documentId);
  }, [loadProcessingStatus]);

  useEffect(() => {
    void loadDocuments();
  }, [loadDocuments]);

  useEffect(() => {
    const activeIds = documents
      .filter((document) => document.processingStatus === 'PROCESSING')
      .map((document) => document.id);
    if (activeIds.length === 0) {
      return;
    }
    let cancelled = false;
    const poll = async () => {
      for (const documentId of activeIds) {
        try {
          const status = await loadProcessingStatus(documentId);
          if (!cancelled && selectedDocumentId === documentId && status.ready) {
            await loadClauses(documentId);
          }
        } catch (_error) {
          // Keep polling; transient status failures should not break the page.
        }
      }
    };
    void poll();
    const interval = window.setInterval(() => {
      void poll();
    }, 3000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [documents, loadClauses, loadProcessingStatus, selectedDocumentId]);

  useEffect(() => {
    if (selectedDocumentId) {
      void loadClauses(selectedDocumentId);
    } else {
      setClauses([]);
    }
  }, [loadClauses, selectedDocumentId]);

  const openCreateDocument = useCallback(() => {
    setEditingDocument(null);
    documentForm.setFieldsValue({
      companyName: '',
      productName: '',
      versionLabel: '',
      fileName: '',
      fileType: '',
      fileSize: null,
      storagePath: '',
      extractedText: '',
      extractionStatus: 'PENDING',
      extractionError: ''
    });
    setDocumentModalOpen(true);
  }, [documentForm]);

  const openUploadDocument = useCallback(() => {
    uploadForm.resetFields();
    setUploadFileList([]);
    setUploadModalOpen(true);
  }, [uploadForm]);

  const openEditDocument = useCallback(
    (document: InsuranceConditionDocument) => {
      setEditingDocument(document);
      documentForm.setFieldsValue({
        companyName: document.companyName,
        productName: document.productName,
        versionLabel: document.versionLabel || '',
        fileName: document.fileName,
        fileType: document.fileType || '',
        fileSize: document.fileSize,
        storagePath: document.storagePath || '',
        extractedText: document.extractedText || '',
        extractionStatus: document.extractionStatus,
        extractionError: document.extractionError || ''
      });
      setDocumentModalOpen(true);
    },
    [documentForm]
  );

  const openCreateClause = useCallback(() => {
    if (!selectedDocument) {
      return;
    }
    setEditingClause(null);
    clauseForm.setFieldsValue({
      areaCode: '',
      sectionTitle: '',
      pageNo: null,
      clauseOrder: clauses.length + 1,
      rawText: ''
    });
    setClauseModalOpen(true);
  }, [clauseForm, clauses.length, selectedDocument]);

  const openEditClause = useCallback(
    (clause: InsuranceConditionClause) => {
      setEditingClause(clause);
      clauseForm.setFieldsValue({
        areaCode: clause.areaCode || '',
        sectionTitle: clause.sectionTitle || '',
        pageNo: clause.pageNo,
        clauseOrder: clause.clauseOrder,
        rawText: clause.rawText
      });
      setClauseModalOpen(true);
    },
    [clauseForm]
  );

  const openStructure = useCallback(
    async (clause: InsuranceConditionClause) => {
      let latestClause = clause;
      if (selectedDocument) {
        setStructureLoadingClauseId(clause.id);
        try {
          const response = await api(`/api/insurance-conditions/${selectedDocument.id}/clauses/${clause.id}`);
          const data = await response.json().catch(() => ({}));
          if (!response.ok) {
            throw new Error((data as any)?.error || 'Kunne ikke hente klausulstruktur');
          }
          latestClause = normalizeClause(data);
          setClauses((current) => current.map((item) => (item.id === latestClause.id ? latestClause : item)));
        } catch (error: any) {
          message.error(error?.message || 'Kunne ikke hente klausulstruktur');
          return;
        } finally {
          setStructureLoadingClauseId(null);
        }
      }

      setStructuringClause(latestClause);
      const structure = latestClause.structure;
      structureForm.setFieldsValue({
        covers: structure?.covers === true ? 'true' : structure?.covers === false ? 'false' : 'null',
        coverageSummary: structure?.coverageSummary || '',
        exclusionsText: arrayToLines(structure?.exclusions),
        deductibleAmount: structure?.deductibleAmount ?? null,
        deductibleText: structure?.deductibleText || '',
        maxCoverageAmount: structure?.maxCoverageAmount ?? null,
        maxCoverageText: structure?.maxCoverageText || '',
        requirementsText: arrayToLines(structure?.requirements),
        practicalNotes: structure?.practicalNotes || '',
        confidence: structure?.confidence ?? null,
        model: structure?.model || '',
        payloadText: JSON.stringify(structure?.payload || {}, null, 2)
      });
      setStructureModalOpen(true);
    },
    [selectedDocument, structureForm]
  );

  const saveDocument = useCallback(async () => {
    try {
      const values = await documentForm.validateFields();
      setSaving(true);
      const payload = {
        companyName: values.companyName,
        productName: values.productName,
        versionLabel: values.versionLabel || null,
        fileName: values.fileName,
        fileType: values.fileType || null,
        fileSize: values.fileSize ?? null,
        storagePath: values.storagePath || null,
        extractedText: values.extractedText || null,
        extractionStatus: values.extractionStatus,
        extractionError: values.extractionError || null
      };
      const response = await api(editingDocument ? `/api/insurance-conditions/${editingDocument.id}` : '/api/insurance-conditions', {
        method: editingDocument ? 'PATCH' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error((data as any)?.error || 'Kunne ikke gemme betingelsesdokument');
      }
      message.success(editingDocument ? 'Betingelsesdokument opdateret' : 'Betingelsesdokument oprettet');
      setDocumentModalOpen(false);
      setEditingDocument(null);
      await loadDocuments();
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }
      message.error(error?.message || 'Kunne ikke gemme betingelsesdokument');
    } finally {
      setSaving(false);
    }
  }, [documentForm, editingDocument, loadDocuments]);

  const uploadDocument = useCallback(async () => {
    try {
      const values = await uploadForm.validateFields();
      const file = uploadFileList[0]?.originFileObj;
      if (!file) {
        message.warning('Vælg en fil');
        return;
      }

      setUploadSaving(true);
      const formData = new FormData();
      formData.append('companyName', values.companyName);
      formData.append('productName', values.productName);
      if (values.versionLabel) {
        formData.append('versionLabel', values.versionLabel);
      }
      formData.append('file', file);

      const response = await api('/api/insurance-conditions/upload', {
        method: 'POST',
        body: formData
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error((data as any)?.error || 'Kunne ikke uploade betingelser');
      }
      const document = normalizeDocument(data);
      message.success(document.extractionStatus === 'COMPLETED' ? 'Betingelser uploadet' : 'Betingelser uploadet, men tekstudtræk fejlede');
      setUploadModalOpen(false);
      uploadForm.resetFields();
      setUploadFileList([]);
      await loadDocuments();
      setSelectedDocumentId(document.id);
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }
      message.error(toInsuranceConditionsUiErrorMessage(error, 'Kunne ikke uploade betingelser'));
    } finally {
      setUploadSaving(false);
    }
  }, [loadDocuments, uploadFileList, uploadForm]);

  const segmentSelectedDocument = useCallback(async (replace = false) => {
    if (!selectedDocument) {
      return;
    }
    setSegmentLoading(true);
    try {
      const response = await api(`/api/insurance-conditions/${selectedDocument.id}/segment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ replace })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error((data as any)?.error || 'Kunne ikke segmentere dokumentet');
      }
      message.success(`Klausuler oprettet: ${Number((data as any)?.createdCount ?? 0)}`);
      await Promise.all([loadDocuments(), loadClauses(selectedDocument.id)]);
    } catch (error: any) {
      message.error(error?.message || 'Kunne ikke segmentere dokumentet');
    } finally {
      setSegmentLoading(false);
    }
  }, [loadClauses, loadDocuments, selectedDocument]);

  const deleteDocument = useCallback(
    async (document: InsuranceConditionDocument) => {
      try {
        const response = await api(`/api/insurance-conditions/${document.id}`, { method: 'DELETE' });
        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error((data as any)?.error || 'Kunne ikke slette betingelsesdokument');
        }
        message.success('Betingelsesdokument slettet');
        if (selectedDocumentId === document.id) {
          setSelectedDocumentId(null);
          setClauses([]);
        }
        await loadDocuments();
      } catch (error: any) {
        message.error(error?.message || 'Kunne ikke slette betingelsesdokument');
      }
    },
    [loadDocuments, selectedDocumentId]
  );

  const saveClause = useCallback(async () => {
    if (!selectedDocument) {
      return;
    }
    try {
      const values = await clauseForm.validateFields();
      setSaving(true);
      const payload = {
        areaCode: values.areaCode || null,
        sectionTitle: values.sectionTitle || null,
        pageNo: values.pageNo ?? null,
        clauseOrder: values.clauseOrder ?? null,
        rawText: values.rawText
      };
      const response = await api(
        editingClause
          ? `/api/insurance-conditions/${selectedDocument.id}/clauses/${editingClause.id}`
          : `/api/insurance-conditions/${selectedDocument.id}/clauses`,
        {
          method: editingClause ? 'PATCH' : 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error((data as any)?.error || 'Kunne ikke gemme klausul');
      }
      message.success(editingClause ? 'Klausul opdateret' : 'Klausul oprettet');
      setClauseModalOpen(false);
      setEditingClause(null);
      await Promise.all([loadClauses(selectedDocument.id), loadDocuments()]);
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }
      message.error(error?.message || 'Kunne ikke gemme klausul');
    } finally {
      setSaving(false);
    }
  }, [clauseForm, editingClause, loadClauses, loadDocuments, selectedDocument]);

  const deleteClause = useCallback(
    async (clause: InsuranceConditionClause) => {
      if (!selectedDocument) {
        return;
      }
      try {
        const response = await api(`/api/insurance-conditions/${selectedDocument.id}/clauses/${clause.id}`, { method: 'DELETE' });
        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error((data as any)?.error || 'Kunne ikke slette klausul');
        }
        message.success('Klausul slettet');
        await Promise.all([loadClauses(selectedDocument.id), loadDocuments()]);
      } catch (error: any) {
        message.error(error?.message || 'Kunne ikke slette klausul');
      }
    },
    [loadClauses, loadDocuments, selectedDocument]
  );

  const saveStructure = useCallback(async () => {
    if (!selectedDocument || !structuringClause) {
      return;
    }
    try {
      const values = await structureForm.validateFields();
      let payload: Record<string, unknown> = {};
      if (values.payloadText?.trim()) {
        payload = JSON.parse(values.payloadText);
        if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
          throw new Error('Payload skal være et JSON-objekt');
        }
      }
      setSaving(true);
      const covers = values.covers === 'true' ? true : values.covers === 'false' ? false : null;
      const response = await api(`/api/insurance-conditions/${selectedDocument.id}/clauses/${structuringClause.id}/structure`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          covers,
          coverageSummary: values.coverageSummary || null,
          exclusions: linesToArray(values.exclusionsText),
          deductibleAmount: values.deductibleAmount ?? null,
          deductibleText: values.deductibleText || null,
          maxCoverageAmount: values.maxCoverageAmount ?? null,
          maxCoverageText: values.maxCoverageText || null,
          requirements: linesToArray(values.requirementsText),
          practicalNotes: values.practicalNotes || null,
          confidence: values.confidence ?? null,
          model: values.model || null,
          payload
        })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error((data as any)?.error || 'Kunne ikke gemme struktur');
      }
      message.success('Struktur gemt');
      setStructureModalOpen(false);
      setStructuringClause(null);
      await loadClauses(selectedDocument.id);
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }
      message.error(error?.message || 'Kunne ikke gemme struktur');
    } finally {
      setSaving(false);
    }
  }, [loadClauses, selectedDocument, structureForm, structuringClause]);

  const analyzeStructure = useCallback(async () => {
    if (!selectedDocument || !structuringClause) {
      return;
    }
    setStructureAnalyzing(true);
    try {
      const response = await api(`/api/insurance-conditions/${selectedDocument.id}/clauses/${structuringClause.id}/structure/analyze`, {
        method: 'POST'
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error((data as any)?.error || 'Kunne ikke analysere klausulstruktur');
      }
      structureForm.setFieldsValue({
        covers: data.covers === true ? 'true' : data.covers === false ? 'false' : 'null',
        coverageSummary: data.coverageSummary || '',
        exclusionsText: arrayToLines(data.exclusions),
        deductibleAmount: data.deductibleAmount ?? null,
        deductibleText: data.deductibleText || '',
        maxCoverageAmount: data.maxCoverageAmount ?? null,
        maxCoverageText: data.maxCoverageText || '',
        requirementsText: arrayToLines(data.requirements),
        practicalNotes: data.practicalNotes || '',
        confidence: data.confidence ?? null,
        model: data.model || '',
        payloadText: JSON.stringify(data.payload || {}, null, 2)
      });
      message.success('Klausulstruktur analyseret');
      await loadClauses(selectedDocument.id);
    } catch (error: any) {
      message.error(error?.message || 'Kunne ikke analysere klausulstruktur');
    } finally {
      setStructureAnalyzing(false);
    }
  }, [loadClauses, selectedDocument, structureForm, structuringClause]);

  const generateDocumentEmbeddings = useCallback(async (force = false) => {
    if (!selectedDocument) {
      return;
    }
    setEmbeddingLoading(true);
    try {
      const response = await api(`/api/insurance-conditions/${selectedDocument.id}/embeddings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ force })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error((data as any)?.error || 'Kunne ikke generere embeddings');
      }
      message.success(`Embeddings genereret: ${Number((data as any)?.embeddedCount ?? 0)}`);
      await loadClauses(selectedDocument.id);
    } catch (error: any) {
      message.error(error?.message || 'Kunne ikke generere embeddings');
    } finally {
      setEmbeddingLoading(false);
    }
  }, [loadClauses, selectedDocument]);

  const generateClauseEmbedding = useCallback(async (clause: InsuranceConditionClause) => {
    if (!selectedDocument) {
      return;
    }
    setEmbeddingLoading(true);
    try {
      const response = await api(`/api/insurance-conditions/${selectedDocument.id}/clauses/${clause.id}/embedding`, {
        method: 'POST'
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error((data as any)?.error || 'Kunne ikke generere embedding');
      }
      message.success('Embedding genereret');
      await loadClauses(selectedDocument.id);
    } catch (error: any) {
      message.error(error?.message || 'Kunne ikke generere embedding');
    } finally {
      setEmbeddingLoading(false);
    }
  }, [loadClauses, selectedDocument]);

  const runSemanticSearch = useCallback(async () => {
    const query = semanticQuery.trim();
    if (!query) {
      message.warning('Angiv søgetekst');
      return;
    }
    setSemanticLoading(true);
    try {
      const response = await api('/api/insurance-conditions/semantic-search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query, limit: 10 })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error((data as any)?.error || 'Kunne ikke søge i betingelser');
      }
      const results = Array.isArray((data as any)?.results)
        ? (data as any).results.map((item: any) => ({ ...normalizeClause(item), similarity: Number(item.similarity ?? 0) }))
        : [];
      setSemanticResults(results);
    } catch (error: any) {
      message.error(error?.message || 'Kunne ikke søge i betingelser');
    } finally {
      setSemanticLoading(false);
    }
  }, [semanticQuery]);

  const runDocumentMatch = useCallback(async () => {
    if (!selectedDocument || !matchTargetDocumentId) {
      message.warning('Vælg et måldokument');
      return;
    }
    if (selectedDocument.processingStatus !== 'READY' || matchTargetDocument?.processingStatus !== 'READY') {
      message.warning('Begge dokumenter bør være færdigbehandlede før match');
    }
    setMatchLoading(true);
    setMatchCustomerSummary(null);
    setMatchCustomerReport(null);
    try {
      const response = await api('/api/insurance-conditions/match', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sourceDocumentId: selectedDocument.id, targetDocumentId: matchTargetDocumentId, limit: 100 })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error((data as any)?.error || 'Kunne ikke matche betingelser');
      }
      setMatchResults(Array.isArray((data as any)?.matches) ? (data as any).matches.map(normalizeMatchResult) : []);
      setReverseMatchResults(Array.isArray((data as any)?.reverseMatches) ? (data as any).reverseMatches.map(normalizeMatchResult) : []);
      setMatchCustomerSummary(normalizeCustomerMatchSummary((data as any)?.customerSummary));
      setMatchCustomerReport(normalizeCustomerComparisonReport((data as any)?.customerReport));
    } catch (error: any) {
      message.error(error?.message || 'Kunne ikke matche betingelser');
    } finally {
      setMatchLoading(false);
    }
  }, [matchTargetDocument, matchTargetDocumentId, selectedDocument]);

  const downloadCustomerReportPdf = useCallback(async () => {
    if (!selectedDocument || !matchTargetDocumentId) {
      message.warning('Vælg først to dokumenter og kør match');
      return;
    }
    setCustomerReportDownloading(true);
    try {
      const blob = await downloadInsuranceConditionCustomerReportPdf({
        sourceDocumentId: selectedDocument.id,
        targetDocumentId: matchTargetDocumentId,
        limit: 100
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${fileSlug(companyLabel(selectedDocument))}-vs-${fileSlug(companyLabel(matchTargetDocument))}-kunderesume.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      message.error(error?.message || 'Kunne ikke downloade kunderesume');
    } finally {
      setCustomerReportDownloading(false);
    }
  }, [matchTargetDocument, matchTargetDocumentId, selectedDocument]);

  const documentColumns: ColumnsType<InsuranceConditionDocument> = [
    {
      title: 'Selskab',
      dataIndex: 'companyName',
      key: 'companyName',
      sorter: (left, right) => left.companyName.localeCompare(right.companyName, 'da', { sensitivity: 'base' })
    },
    {
      title: 'Produkt',
      dataIndex: 'productName',
      key: 'productName',
      sorter: (left, right) => left.productName.localeCompare(right.productName, 'da', { sensitivity: 'base' })
    },
    {
      title: 'Version',
      dataIndex: 'versionLabel',
      key: 'versionLabel',
      render: (value: string | null) => compactText(value)
    },
    {
      title: 'Fil',
      dataIndex: 'fileName',
      key: 'fileName',
      ellipsis: true
    },
    {
      title: 'Status',
      dataIndex: 'extractionStatus',
      key: 'extractionStatus',
      render: (value: InsuranceConditionDocument['extractionStatus']) => <Tag color={statusColors[value]}>{statusLabels[value]}</Tag>,
      filters: [
        { text: statusLabels.PENDING, value: 'PENDING' },
        { text: statusLabels.COMPLETED, value: 'COMPLETED' },
        { text: statusLabels.FAILED, value: 'FAILED' }
      ],
      onFilter: (value, record) => record.extractionStatus === value
    },
    {
      title: 'Behandling',
      dataIndex: 'processingStatus',
      key: 'processingStatus',
      width: 150,
      render: (value: InsuranceConditionDocument['processingStatus'], record) => {
        const status = processingStatuses[record.id];
        return (
          <Space direction="vertical" size={0}>
            <Tag color={processingColors[value]}>{processingLabels[value]}</Tag>
            {value === 'PROCESSING' && status && <Progress percent={status.progress} size="small" showInfo={false} />}
          </Space>
        );
      }
    },
    {
      title: 'Klausuler',
      dataIndex: 'clauseCount',
      key: 'clauseCount',
      width: 110,
      sorter: (left, right) => left.clauseCount - right.clauseCount
    },
    {
      title: 'Uploadet',
      dataIndex: 'uploadedAt',
      key: 'uploadedAt',
      render: formatDateTime,
      width: 180
    },
    {
      title: 'Handlinger',
      key: 'actions',
      width: 250,
      render: (_value, record) => (
        <Space size="small" onClick={(event) => event.stopPropagation()}>
          <Button size="small" icon={<FileSearchOutlined />} onClick={() => setSelectedDocumentId(record.id)}>
            Åbn
          </Button>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEditDocument(record)}>
            Rediger
          </Button>
          <Popconfirm
            title="Slet betingelser"
            description={`${record.companyName} · ${record.productName}`}
            okText="Slet"
            cancelText="Annuller"
            onConfirm={() => deleteDocument(record)}
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  const clauseColumns: ColumnsType<InsuranceConditionClause> = [
    {
      title: '#',
      dataIndex: 'clauseOrder',
      key: 'clauseOrder',
      width: 70,
      render: (value: number | null) => value ?? '—'
    },
    {
      title: 'Område',
      dataIndex: 'areaCode',
      key: 'areaCode',
      width: 150,
      render: (value: string | null) => compactText(value)
    },
    {
      title: 'Sektion',
      dataIndex: 'sectionTitle',
      key: 'sectionTitle',
      width: 220,
      render: (value: string | null) => compactText(value)
    },
    {
      title: 'Tekst',
      dataIndex: 'rawText',
      key: 'rawText',
      ellipsis: true,
      render: (value: string, record) => (
        <Space direction="vertical" size={0} style={{ maxWidth: 560 }}>
          <Text strong>{compactText(record.structure?.coverageSummary || value)}</Text>
          <Text type="secondary" ellipsis>
            {value}
          </Text>
        </Space>
      )
    },
    {
      title: 'Dækker',
      key: 'covers',
      width: 100,
      render: (_value, record) =>
        record.structure?.covers === true ? (
          <Tag color="green">Ja</Tag>
        ) : record.structure?.covers === false ? (
          <Tag color="red">Nej</Tag>
        ) : (
          <Tag>Ukendt</Tag>
        )
    },
    {
      title: 'Embedding',
      key: 'embedding',
      width: 130,
      render: (_value, record) => record.hasEmbedding ? <Tag color="green">Klar</Tag> : <Tag color="gold">Mangler</Tag>
    },
    {
      title: 'Handlinger',
      key: 'actions',
      width: 350,
      render: (_value, record) => (
        <Space size="small">
          <Button size="small" loading={structureLoadingClauseId === record.id} onClick={() => void openStructure(record)}>
            Struktur
          </Button>
          <Button size="small" loading={embeddingLoading} onClick={() => generateClauseEmbedding(record)}>
            Embedding
          </Button>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEditClause(record)}>
            Rediger
          </Button>
          <Popconfirm
            title="Slet klausul"
            okText="Slet"
            cancelText="Annuller"
            onConfirm={() => deleteClause(record)}
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Card
        title="Betingelser"
        extra={
          <Space>
            <Input.Search
              allowClear
              placeholder="Søg"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              onSearch={(value) => setSearchTerm(value.trim())}
              style={{ width: 280 }}
            />
            <Button icon={<ReloadOutlined />} onClick={() => loadDocuments()} />
            <Button icon={<UploadOutlined />} onClick={openUploadDocument}>
              Upload betingelser
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreateDocument}>
              Nyt dokument
            </Button>
          </Space>
        }
      >
        <Table
          rowKey="id"
          loading={documentsLoading}
          columns={documentColumns}
          dataSource={documents}
          pagination={{ pageSize: 12, showSizeChanger: false }}
          rowClassName={(record) => (record.id === selectedDocumentId ? 'ant-table-row-selected' : '')}
          onRow={(record) => ({
            onClick: () => setSelectedDocumentId(record.id),
            style: { cursor: 'pointer' }
          })}
        />
      </Card>

      {selectedDocument && (
        <Card
          title={`${selectedDocument.companyName} · ${selectedDocument.productName}`}
          extra={
            <Space>
              <Tag color={statusColors[selectedDocument.extractionStatus]}>{statusLabels[selectedDocument.extractionStatus]}</Tag>
              <Tag color={processingColors[selectedDocument.processingStatus]}>{processingLabels[selectedDocument.processingStatus]}</Tag>
              <Button
                loading={segmentLoading || selectedDocument.processingStatus === 'PROCESSING'}
                onClick={async () => {
                  try {
                    setSegmentLoading(true);
                    await startDocumentProcessing(selectedDocument.id, true);
                    message.success('Behandling startet');
                  } catch (error: any) {
                    message.error(error?.message || 'Kunne ikke starte behandling');
                  } finally {
                    setSegmentLoading(false);
                  }
                }}
              >
                Behandl igen
              </Button>
              {selectedDocument.clauseCount > 0 ? (
                <Popconfirm
                  title="Segmentér dokumentet igen"
                  description="Eksisterende klausuler, strukturer og embeddings erstattes."
                  okText="Segmentér igen"
                  cancelText="Annuller"
                  onConfirm={() => segmentSelectedDocument(true)}
                >
                  <Button loading={segmentLoading}>Segmentér igen</Button>
                </Popconfirm>
              ) : (
                <Button loading={segmentLoading} onClick={() => segmentSelectedDocument(false)}>
                  Segmentér tekst
                </Button>
              )}
              <Button loading={embeddingLoading} onClick={() => generateDocumentEmbeddings(false)}>
                Generér embeddings
              </Button>
              <Button icon={<ReloadOutlined />} onClick={() => loadClauses(selectedDocument.id)} />
              <Button type="primary" icon={<PlusOutlined />} onClick={openCreateClause}>
                Ny klausul
              </Button>
            </Space>
          }
        >
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col xs={24} md={8}>
              <Text type="secondary">Version</Text>
              <div>{compactText(selectedDocument.versionLabel)}</div>
            </Col>
            <Col xs={24} md={8}>
              <Text type="secondary">Fil</Text>
              <div>{compactText(selectedDocument.fileName)}</div>
            </Col>
            <Col xs={24} md={8}>
              <Text type="secondary">Klausuler</Text>
              <div>{selectedDocument.clauseCount}</div>
            </Col>
          </Row>
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col xs={24} md={8}>
              <Text type="secondary">Struktur</Text>
              <div>{selectedDocument.structuredCount} / {selectedDocument.clauseCount}</div>
            </Col>
            <Col xs={24} md={8}>
              <Text type="secondary">Embeddings</Text>
              <div>{selectedDocument.embeddedCount} / {selectedDocument.clauseCount}</div>
            </Col>
            <Col xs={24} md={8}>
              <Text type="secondary">Behandling</Text>
              <div>
                <Tag color={processingColors[selectedDocument.processingStatus]}>{processingLabels[selectedDocument.processingStatus]}</Tag>
              </div>
            </Col>
          </Row>
          {selectedDocument.processingStatus === 'PROCESSING' && selectedProcessingStatus && (
            <Progress percent={selectedProcessingStatus.progress} style={{ marginBottom: 16 }} />
          )}
          {selectedDocument.processingError && (
            <>
              <Tag color="red">{selectedDocument.processingError}</Tag>
              <Divider />
            </>
          )}
          {selectedDocument.extractionError && (
            <>
              <Tag color="red">{selectedDocument.extractionError}</Tag>
              <Divider />
            </>
          )}
          <Table
            rowKey="id"
            loading={clausesLoading}
            columns={clauseColumns}
            dataSource={clauses}
            pagination={{ pageSize: 10, showSizeChanger: false }}
          />
        </Card>
      )}

      <Card title="Semantisk søgning">
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Space.Compact style={{ width: '100%' }}>
            <Input
              value={semanticQuery}
              onChange={(event) => setSemanticQuery(event.target.value)}
              onPressEnter={runSemanticSearch}
              placeholder="Søg efter fx rørskade, langsom udsivning eller selvrisiko"
            />
            <Button type="primary" loading={semanticLoading} onClick={runSemanticSearch}>
              Søg
            </Button>
          </Space.Compact>
          <Table
            rowKey="id"
            size="small"
            loading={semanticLoading}
            columns={[
              {
                title: 'Match',
                dataIndex: 'similarity',
                key: 'similarity',
                width: 100,
                render: (value: number) => `${Math.round(Number(value) * 100)}%`,
                sorter: (left: any, right: any) => Number(left.similarity) - Number(right.similarity)
              },
              {
                title: 'Selskab',
                dataIndex: 'companyName',
                key: 'companyName',
                width: 160
              },
              {
                title: 'Produkt',
                dataIndex: 'productName',
                key: 'productName',
                width: 180
              },
              {
                title: 'Klausul',
                dataIndex: 'rawText',
                key: 'rawText',
                ellipsis: true,
                render: (value: string, record: any) => record.coverageSummary || value
              }
            ]}
            dataSource={semanticResults}
            pagination={{ pageSize: 5, showSizeChanger: false }}
          />
        </Space>
      </Card>

      {selectedDocument && (
        <Card title="Match betingelser">
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Space style={{ width: '100%' }}>
              <Select
                allowClear
                showSearch
                placeholder="Vælg måldokument"
                value={matchTargetDocumentId || undefined}
                onChange={(value) => {
                  setMatchTargetDocumentId(value || null);
                  setMatchResults([]);
                  setReverseMatchResults([]);
                  setMatchCustomerSummary(null);
                  setMatchCustomerReport(null);
                }}
                optionFilterProp="label"
                style={{ minWidth: 360 }}
                options={documents
                  .filter((document) => document.id !== selectedDocument.id)
                  .map((document) => ({
                    value: document.id,
                    label: `${document.companyName} · ${document.productName}${document.versionLabel ? ` · ${document.versionLabel}` : ''}`
                  }))}
              />
              <Button type="primary" loading={matchLoading} onClick={runDocumentMatch}>
                Match
              </Button>
            </Space>
            {selectedDocument.processingStatus !== 'READY' || matchTargetDocument?.processingStatus !== 'READY' ? (
              <Tag color="gold">Begge dokumenter bør være klar til match før sammenligning</Tag>
            ) : null}
            {(matchCustomerReport || matchCustomerSummary) && matchTargetDocument ? (
              <div style={{ border: '1px solid #f0f0f0', borderRadius: 6, padding: 16, background: '#fff' }}>
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  <Space wrap style={{ width: '100%', justifyContent: 'space-between' }}>
                    <Space wrap>
                      <Text strong>Kunderesume</Text>
                      <Tag color="blue">{matchResults.length} match {selectedDocument.companyName} {'->'} {matchTargetDocument.companyName}</Tag>
                      <Tag color="purple">{reverseMatchResults.length} match {matchTargetDocument.companyName} {'->'} {selectedDocument.companyName}</Tag>
                    </Space>
                    <Button
                      icon={<DownloadOutlined />}
                      loading={customerReportDownloading}
                      onClick={downloadCustomerReportPdf}
                      disabled={!matchTargetDocumentId || matchLoading}
                    >
                      Download kunderesume som PDF
                    </Button>
                  </Space>
                  {matchCustomerReport ? (
                    <CustomerReportPreview report={matchCustomerReport} />
                  ) : matchCustomerSummary ? (
                    <>
                      <Text type="secondary">{matchCustomerSummary.note}</Text>
                      <Row gutter={24}>
                        <Col xs={24} md={12}>
                          <CustomerProsCons
                            title={companyLabel(selectedDocument)}
                            advantages={matchCustomerSummary.source.advantages}
                            disadvantages={matchCustomerSummary.source.disadvantages}
                          />
                        </Col>
                        <Col xs={24} md={12}>
                          <CustomerProsCons
                            title={companyLabel(matchTargetDocument)}
                            advantages={matchCustomerSummary.target.advantages}
                            disadvantages={matchCustomerSummary.target.disadvantages}
                          />
                        </Col>
                      </Row>
                    </>
                  ) : null}
                </Space>
              </div>
            ) : null}
            {matchResults.length > 0 ? <Divider orientation="left">Detaljeret mægleranalyse</Divider> : null}
            <Table
              rowKey={(record) => `${record.sourceClause?.id}-${record.targetClause?.id}`}
              size="small"
              loading={matchLoading}
              scroll={{ x: 1120 }}
              expandable={{
                expandedRowRender: (record) => (
                  <Row gutter={24}>
                    <Col xs={24} md={8}>
                      <Text strong>Kilde</Text>
                      <div style={{ marginTop: 8 }}>{record.sourceClause?.rawText}</div>
                    </Col>
                    <Col xs={24} md={8}>
                      <Text strong>Matchende klausul</Text>
                      <div style={{ marginTop: 8 }}>{record.targetClause?.rawText}</div>
                    </Col>
                    <Col xs={24} md={8}>
                      <Text strong>Analyse</Text>
                      <Space direction="vertical" size={4} style={{ marginTop: 8 }}>
                        {(record.comparison?.differences || []).map((item, index) => (
                          <Text key={`difference-${index}`}>{item}</Text>
                        ))}
                        {(record.comparison?.brokerWatch || []).map((item, index) => (
                          <Text key={`watch-${index}`} type="warning">{item}</Text>
                        ))}
                      </Space>
                    </Col>
                    <Col xs={24}>
                      <Divider orientation="left">Kundevendt fordele og ulemper</Divider>
                      <Text type="secondary">{record.comparison?.customerView?.summary}</Text>
                      <Row gutter={24} style={{ marginTop: 12 }}>
                        <Col xs={24} md={12}>
                          <CustomerProsCons
                            title={companyLabel(record.sourceClause)}
                            advantages={record.comparison?.customerView?.chooseSourceAdvantages || []}
                            disadvantages={record.comparison?.customerView?.chooseSourceDisadvantages || []}
                          />
                        </Col>
                        <Col xs={24} md={12}>
                          <CustomerProsCons
                            title={companyLabel(record.targetClause)}
                            advantages={record.comparison?.customerView?.chooseTargetAdvantages || []}
                            disadvantages={record.comparison?.customerView?.chooseTargetDisadvantages || []}
                          />
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                )
              }}
              columns={[
                {
                  title: 'Match',
                  dataIndex: 'similarity',
                  key: 'similarity',
                  width: 100,
                  render: (value: number) => `${Math.round(Number(value) * 100)}%`
                },
                {
                  title: 'Kilde',
                  key: 'source',
                  ellipsis: true,
                  render: (_value: unknown, record: any) => compactText(record.sourceClause?.sectionTitle || record.sourceClause?.rawText)
                },
                {
                  title: 'Matchende klausul',
                  key: 'target',
                  ellipsis: true,
                  render: (_value: unknown, record: any) => compactText(record.targetClause?.sectionTitle || record.targetClause?.rawText)
                },
                {
                  title: 'Forskel',
                  key: 'comparison',
                  width: 320,
                  render: (_value: unknown, record: ClauseMatchResult) => (
                    <Space direction="vertical" size={4}>
                      <Space>
                        <Tag color={riskColor(record.comparison?.riskLevel)}>{riskLabel(record.comparison?.riskLevel)}</Tag>
                        <Text strong>{record.comparison?.summary || 'Ingen analyse'}</Text>
                      </Space>
                      {(record.comparison?.differences || []).slice(0, 2).map((item, index) => (
                        <Text key={index} type="secondary">{item}</Text>
                      ))}
                    </Space>
                  )
                },
                {
                  title: 'Mægler fokus',
                  key: 'brokerWatch',
                  width: 340,
                  render: (_value: unknown, record: ClauseMatchResult) => (
                    <Space direction="vertical" size={4}>
                      {(record.comparison?.brokerWatch || []).slice(0, 3).map((item, index) => (
                        <Text key={index}>{item}</Text>
                      ))}
                    </Space>
                  )
                }
              ]}
              dataSource={matchResults}
              pagination={{ pageSize: 8, showSizeChanger: false }}
            />
          </Space>
        </Card>
      )}

      <Modal
        open={uploadModalOpen}
        title="Upload betingelser"
        okText="Upload"
        cancelText="Annuller"
        onOk={uploadDocument}
        onCancel={() => {
          setUploadModalOpen(false);
          setUploadFileList([]);
          uploadForm.resetFields();
        }}
        confirmLoading={uploadSaving}
        destroyOnClose
        maskClosable={false}
      >
        <Form form={uploadForm} layout="vertical">
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label="Selskab" name="companyName" rules={[{ required: true, message: 'Angiv selskab' }]}>
                <Input autoFocus maxLength={160} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Produkt" name="productName" rules={[{ required: true, message: 'Angiv produkt' }]}>
                <Input maxLength={160} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="Version" name="versionLabel">
            <Input maxLength={120} />
          </Form.Item>
          <Form.Item label="Fil" required>
            <Upload
              accept=".pdf,.docx,.txt,.md,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,text/markdown"
              beforeUpload={() => false}
              fileList={uploadFileList}
              maxCount={1}
              onChange={({ fileList }) => setUploadFileList(fileList.slice(-1))}
            >
              <Button icon={<UploadOutlined />}>Vælg fil</Button>
            </Upload>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={documentModalOpen}
        title={editingDocument ? 'Rediger betingelsesdokument' : 'Nyt betingelsesdokument'}
        okText="Gem"
        cancelText="Annuller"
        onOk={saveDocument}
        onCancel={() => setDocumentModalOpen(false)}
        confirmLoading={saving}
        width={820}
        destroyOnClose
        maskClosable={false}
      >
        <Form form={documentForm} layout="vertical" initialValues={{ extractionStatus: 'PENDING' }}>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label="Selskab" name="companyName" rules={[{ required: true, message: 'Angiv selskab' }]}>
                <Input autoFocus maxLength={160} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Produkt" name="productName" rules={[{ required: true, message: 'Angiv produkt' }]}>
                <Input maxLength={160} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label="Version" name="versionLabel">
                <Input maxLength={120} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Status" name="extractionStatus" rules={[{ required: true, message: 'Vælg status' }]}>
                <Select
                  options={[
                    { value: 'PENDING', label: statusLabels.PENDING },
                    { value: 'COMPLETED', label: statusLabels.COMPLETED },
                    { value: 'FAILED', label: statusLabels.FAILED }
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label="Filnavn" name="fileName" rules={[{ required: true, message: 'Angiv filnavn' }]}>
                <Input maxLength={240} />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item label="Filtype" name="fileType">
                <Input maxLength={120} />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item label="Filstørrelse" name="fileSize">
                <InputNumber min={0} precision={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="Storage path" name="storagePath">
            <Input maxLength={500} />
          </Form.Item>
          <Form.Item label="Fejl" name="extractionError">
            <Input maxLength={500} />
          </Form.Item>
          <Form.Item label="Udtrukket tekst" name="extractedText">
            <Input.TextArea rows={8} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={clauseModalOpen}
        title={editingClause ? 'Rediger klausul' : 'Ny klausul'}
        okText="Gem"
        cancelText="Annuller"
        onOk={saveClause}
        onCancel={() => setClauseModalOpen(false)}
        confirmLoading={saving}
        width={820}
        destroyOnClose
        maskClosable={false}
      >
        <Form form={clauseForm} layout="vertical">
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item label="Områdekode" name="areaCode">
                <Input maxLength={120} />
              </Form.Item>
            </Col>
            <Col xs={24} md={10}>
              <Form.Item label="Sektion" name="sectionTitle">
                <Input maxLength={240} />
              </Form.Item>
            </Col>
            <Col xs={12} md={3}>
              <Form.Item label="Side" name="pageNo">
                <InputNumber min={1} precision={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={12} md={3}>
              <Form.Item label="Orden" name="clauseOrder">
                <InputNumber min={1} precision={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="Klausultekst" name="rawText" rules={[{ required: true, message: 'Angiv klausultekst' }]}>
            <Input.TextArea rows={10} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={structureModalOpen}
        title="Klausulstruktur"
        okText="Gem"
        cancelText="Annuller"
        onOk={saveStructure}
        onCancel={() => setStructureModalOpen(false)}
        confirmLoading={saving}
        width={900}
        destroyOnClose
        maskClosable={false}
      >
        <Space style={{ marginBottom: 16 }}>
          <Button loading={structureAnalyzing} onClick={analyzeStructure}>
            Analyser med AI
          </Button>
        </Space>
        <Form form={structureForm} layout="vertical">
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item label="Dækker" name="covers">
                <Select
                  options={[
                    { value: 'null', label: 'Ukendt' },
                    { value: 'true', label: 'Ja' },
                    { value: 'false', label: 'Nej' }
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="Confidence" name="confidence">
                <InputNumber min={0} max={1} step={0.05} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="Model" name="model">
                <Input maxLength={120} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="Dækningsresume" name="coverageSummary">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label="Undtagelser" name="exclusionsText">
                <Input.TextArea rows={4} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Krav" name="requirementsText">
                <Input.TextArea rows={4} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={6}>
              <Form.Item label="Selvrisiko beløb" name="deductibleAmount">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item label="Selvrisiko tekst" name="deductibleText">
                <Input maxLength={160} />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item label="Maksimum beløb" name="maxCoverageAmount">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item label="Maksimum tekst" name="maxCoverageText">
                <Input maxLength={160} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="Praktiske noter" name="practicalNotes">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item label="Payload" name="payloadText">
            <Input.TextArea rows={6} />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
}

function normalizeStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.map((item) => (typeof item === 'string' ? item : JSON.stringify(item))).filter(Boolean);
}

function normalizeMatchComparison(raw: any): ClauseMatchComparison | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const riskLevel = raw.riskLevel === 'HIGH' || raw.riskLevel === 'MEDIUM' || raw.riskLevel === 'LOW' ? raw.riskLevel : 'LOW';
  return {
    riskLevel,
    summary: raw.summary || '',
    differences: normalizeStringArray(raw.differences),
    brokerWatch: normalizeStringArray(raw.brokerWatch),
    customerView: raw.customerView && typeof raw.customerView === 'object'
      ? {
          summary: raw.customerView.summary || '',
          chooseSourceAdvantages: normalizeStringArray(raw.customerView.chooseSourceAdvantages),
          chooseSourceDisadvantages: normalizeStringArray(raw.customerView.chooseSourceDisadvantages),
          chooseTargetAdvantages: normalizeStringArray(raw.customerView.chooseTargetAdvantages),
          chooseTargetDisadvantages: normalizeStringArray(raw.customerView.chooseTargetDisadvantages)
        }
      : null
  };
}

function companyLabel(clause: any) {
  return [clause?.companyName, clause?.productName].filter(Boolean).join(' · ') || 'Selskab';
}

function CustomerReportPreview({ report }: { report: CustomerComparisonReport }) {
  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div style={{ background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 6, padding: 12 }}>
        <Text strong>Kort konklusion</Text>
        <div style={{ marginTop: 6 }}><Text>{report.conclusion}</Text></div>
      </div>
      <div>
        <Text strong>Hvad betyder det for kunden?</Text>
        <div style={{ marginTop: 6 }}><Text>{report.customerMeaning}</Text></div>
      </div>
      <Row gutter={24}>
        <Col xs={24} md={12}>
          <CustomerReportSide title={report.source.headline} advantages={report.source.advantages} disadvantages={report.source.disadvantages} />
        </Col>
        <Col xs={24} md={12}>
          <CustomerReportSide title={report.target.headline} advantages={report.target.advantages} disadvantages={report.target.disadvantages} />
        </Col>
      </Row>
      <div>
        <Text strong>Mægleren bør gennemgå</Text>
        <Space direction="vertical" size={4} style={{ width: '100%', marginTop: 6 }}>
          {(report.brokerReviewPoints.length ? report.brokerReviewPoints : ['Kontroller ordlyd, policetillæg og kundens konkrete behov før anbefaling.']).slice(0, 5).map((point, index) => (
            <Text key={index}>• {point}</Text>
          ))}
        </Space>
      </div>
      <Text type="secondary">{report.disclaimer}</Text>
    </Space>
  );
}

function CustomerReportSide({
  title,
  advantages,
  disadvantages
}: {
  title: string;
  advantages: CustomerReportPoint[];
  disadvantages: CustomerReportPoint[];
}) {
  const renderPoint = (point: CustomerReportPoint, color: string, background: string) => (
    <div style={{ borderLeft: `3px solid ${color}`, background, padding: '8px 10px' }}>
      <Text strong>{point.title}</Text>
      <div><Text>{point.text}</Text></div>
      {point.reference ? <Text type="secondary">Sammenlignet med: {point.reference}</Text> : null}
    </div>
  );

  return (
    <Space direction="vertical" size={8} style={{ width: '100%' }}>
      <Text strong>{title}</Text>
      <Tag color="green">Vigtigste fordele</Tag>
      {(advantages.length ? advantages : [{ title: 'Ingen tydelige fordele', text: 'Der er ikke fundet klare kundevendte fordele i de vigtigste match.', reference: null }]).slice(0, 5).map((point, index) => (
        <div key={`advantage-${index}`}>{renderPoint(point, '#52c41a', '#f6ffed')}</div>
      ))}
      <Tag color="red">Vigtigste ulemper</Tag>
      {(disadvantages.length ? disadvantages : [{ title: 'Ingen tydelige ulemper', text: 'Der er ikke fundet klare kundevendte ulemper i de vigtigste match.', reference: null }]).slice(0, 5).map((point, index) => (
        <div key={`disadvantage-${index}`}>{renderPoint(point, '#ff4d4f', '#fff2f0')}</div>
      ))}
    </Space>
  );
}

function CustomerProsCons({
  title,
  advantages,
  disadvantages
}: {
  title: string;
  advantages: string[];
  disadvantages: string[];
}) {
  const visibleAdvantages = advantages.slice(0, 6);
  const visibleDisadvantages = disadvantages.slice(0, 6);

  const renderCustomerPoint = (item: string) => {
    const lines = item.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
    if (lines.length <= 1) {
      return <Text>{item}</Text>;
    }

    const clauseLine = lines.find((line) => line.startsWith('Klausul:')) || lines[0];
    const comparisonLine = lines.find((line) => line.startsWith('Sammenlignet med:'));
    const assessmentLine = lines.find((line) => line.startsWith('Vurdering:'));
    const remainingLines = lines.filter((line) => line !== clauseLine && line !== comparisonLine && line !== assessmentLine);

    return (
      <Space direction="vertical" size={2} style={{ width: '100%' }}>
        <Text strong>{clauseLine.replace(/^Klausul:\s*/, '')}</Text>
        {assessmentLine ? <Text>{assessmentLine.replace(/^Vurdering:\s*/, '')}</Text> : null}
        {comparisonLine ? <Text type="secondary">{comparisonLine}</Text> : null}
        {remainingLines.map((line, index) => <Text key={index}>{line}</Text>)}
      </Space>
    );
  };

  const renderItems = (items: string[], color: string, background: string) => items.map((item, index) => (
    <div
      key={`${color}-${index}`}
      style={{
        borderLeft: `3px solid ${color}`,
        background,
        padding: '8px 10px',
        lineHeight: 1.45
      }}
    >
      {renderCustomerPoint(item)}
    </div>
  ));

  return (
    <Space direction="vertical" size={8} style={{ width: '100%' }}>
      <Text strong>{title}</Text>
      {visibleAdvantages.length > 0 ? <Tag color="green">Fordele</Tag> : null}
      {renderItems(visibleAdvantages, '#52c41a', '#f6ffed')}
      {visibleDisadvantages.length > 0 ? <Tag color="red">Ulemper</Tag> : null}
      {renderItems(visibleDisadvantages, '#ff4d4f', '#fff2f0')}
      {advantages.length > visibleAdvantages.length ? <Text type="secondary">+{advantages.length - visibleAdvantages.length} yderligere fordele i de fulde match.</Text> : null}
      {disadvantages.length > visibleDisadvantages.length ? <Text type="secondary">+{disadvantages.length - visibleDisadvantages.length} yderligere ulemper i de fulde match.</Text> : null}
      {advantages.length === 0 && disadvantages.length === 0 ? <Text type="secondary">Ingen tydelige kundevendte punkter fundet.</Text> : null}
    </Space>
  );
}

function normalizeMatchResult(raw: any): ClauseMatchResult {
  return {
    sourceClause: raw?.sourceClause || raw?.source_clause || null,
    targetClause: raw?.targetClause || raw?.target_clause || null,
    similarity: Number(raw?.similarity ?? 0),
    comparison: normalizeMatchComparison(raw?.comparison)
  };
}

function normalizeCustomerMatchSummary(raw: any): CustomerMatchSummary | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  return {
    source: {
      advantages: normalizeStringArray(raw.source?.advantages),
      disadvantages: normalizeStringArray(raw.source?.disadvantages)
    },
    target: {
      advantages: normalizeStringArray(raw.target?.advantages),
      disadvantages: normalizeStringArray(raw.target?.disadvantages)
    },
    note: raw.note || ''
  };
}

function normalizeCustomerReportPoint(raw: any): CustomerReportPoint | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const title = typeof raw.title === 'string' ? raw.title : '';
  const text = typeof raw.text === 'string' ? raw.text : '';
  if (!title && !text) {
    return null;
  }
  return {
    title: title || 'Relevant punkt',
    text: text || '',
    reference: typeof raw.reference === 'string' && raw.reference.trim() ? raw.reference : null
  };
}

function normalizeCustomerReportPoints(raw: any): CustomerReportPoint[] {
  return Array.isArray(raw) ? raw.map(normalizeCustomerReportPoint).filter(Boolean) as CustomerReportPoint[] : [];
}

function normalizeCustomerComparisonReport(raw: any): CustomerComparisonReport | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  return {
    title: raw.title || 'Kundevendt sammenligning',
    conclusion: raw.conclusion || '',
    customerMeaning: raw.customerMeaning || '',
    source: {
      headline: raw.source?.headline || 'Kilde',
      advantages: normalizeCustomerReportPoints(raw.source?.advantages),
      disadvantages: normalizeCustomerReportPoints(raw.source?.disadvantages)
    },
    target: {
      headline: raw.target?.headline || 'Mål',
      advantages: normalizeCustomerReportPoints(raw.target?.advantages),
      disadvantages: normalizeCustomerReportPoints(raw.target?.disadvantages)
    },
    brokerReviewPoints: normalizeStringArray(raw.brokerReviewPoints),
    disclaimer: raw.disclaimer || ''
  };
}

function riskColor(riskLevel?: ClauseMatchComparison['riskLevel'] | null) {
  if (riskLevel === 'HIGH') {
    return 'red';
  }
  if (riskLevel === 'MEDIUM') {
    return 'gold';
  }
  return 'green';
}

function riskLabel(riskLevel?: ClauseMatchComparison['riskLevel'] | null) {
  if (riskLevel === 'HIGH') {
    return 'Høj';
  }
  if (riskLevel === 'MEDIUM') {
    return 'Middel';
  }
  return 'Lav';
}